// Browser-side input cleaner — mirrors functions/sanitize.js but throws
// plain Errors instead of HttpsError. Use at the boundary of any Firestore
// write that takes user input (forms, modals, etc.). The Cloud Function
// equivalent re-validates, so this is purely UX/defense-in-depth on the
// client.

const CONTROL_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;
// eslint-disable-next-line no-misleading-character-class
const BIDI_RE = /[‪-‮⁦-⁩]/g;
const HTML_RE = /<[^>]*>/g;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+\d][\d\s().-]{4,24}$/;

export function cleanString(s) {
  if (typeof s !== "string") return "";
  return s.replace(CONTROL_RE, "").replace(BIDI_RE, "").trim();
}

export function stripHtml(s) {
  return cleanString(s).replace(HTML_RE, "");
}

/** Trim + strip HTML + enforce max length. Returns "" if input is nullish. */
export function cleanText(value, maxLen = 500) {
  if (value == null) return "";
  const s = stripHtml(String(value));
  return s.length > maxLen ? s.slice(0, maxLen) : s;
}

/** Trim + lowercase + validate email. Throws if invalid (non-empty). */
export function cleanEmail(value, { required = false } = {}) {
  const s = cleanString(String(value || "")).toLowerCase();
  if (!s) {
    if (required) throw new Error("Email is required");
    return "";
  }
  if (s.length > 254 || !EMAIL_RE.test(s)) {
    throw new Error("Invalid email");
  }
  return s;
}

/** Trim + validate phone (loose international format). */
export function cleanPhone(value) {
  const s = cleanString(String(value || ""));
  if (!s) return "";
  if (s.length > 25 || !PHONE_RE.test(s)) {
    throw new Error("Invalid phone number");
  }
  return s;
}

/** Coerce to a finite number within [min, max]; returns 0 for nullish input. */
export function cleanNumber(value, { min = 0, max = 10_000_000 } = {}) {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) throw new Error("Must be a number");
  if (n < min || n > max) throw new Error(`Must be between ${min} and ${max}`);
  return n;
}

/** Coerce to integer in range. */
export function cleanInt(value, { min = 0, max = 1_000_000 } = {}) {
  const n = cleanNumber(value, { min, max });
  return Math.trunc(n);
}

// Sensible defaults for the user-writable fields the app stores. Keep these
// in sync with the Cloud Function schemas and the rules-layer length caps.
export const FIELD_CAPS = {
  name: 100,
  email: 254,
  phone: 25,
  notes: 2000,
  message: 2000,
  conditions: 1000,
  leaderboardName: 60,
  title: 120,
  inviteCode: 16,
  url: 2000,
};
